// Wave 23 — all-member overrides loader for the admin permissions matrix.
//
// Companion to useCapabilities: where that hook surfaces the *current*
// member's effective capability set, this one loads every override row in
// the active org so the Settings permissions matrix can render a full
// members × capabilities grid.
//
// Delegates straight to `listOverridesForOrg`, which already handles the
// bypass-mode vs Supabase split. Refresh is exposed so mutation handlers
// (grant/revoke) can reload without remounting.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  listOverridesForOrg,
  type CapabilityOverride,
} from "./overridesStore";

export interface UseOrgOverridesResult {
  overrides: ReadonlyArray<CapabilityOverride>;
  loading: boolean;
  refresh: () => Promise<void>;
}

export function useOrgOverrides(orgId: string | null): UseOrgOverridesResult {
  const [overrides, setOverrides] = useState<ReadonlyArray<CapabilityOverride>>(
    [],
  );
  const [loading, setLoading] = useState<boolean>(orgId != null);
  // Guard against stale responses when orgId changes quickly (tab-switch).
  const fetchIdRef = useRef(0);

  const refresh = useCallback(async (): Promise<void> => {
    const fetchId = ++fetchIdRef.current;
    if (!orgId) {
      if (fetchId === fetchIdRef.current) {
        setOverrides([]);
        setLoading(false);
      }
      return;
    }
    setLoading(true);
    try {
      const rows = await listOverridesForOrg(orgId);
      if (fetchId !== fetchIdRef.current) return;
      setOverrides(rows);
    } catch (err) {
      if (fetchId !== fetchIdRef.current) return;
      // eslint-disable-next-line no-console
      console.warn(
        `[permissions] listOverridesForOrg failed: ${err instanceof Error ? err.message : "unknown"}`,
      );
      setOverrides([]);
    } finally {
      if (fetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [orgId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { overrides, loading, refresh };
}
